import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";

// Vimeo video IDs — update these as the portfolio grows
const featuredVideos = [
  {
    id: "1164822580",
    title: "9041 Wyatt Lp SE",
    location: "Tumwater, WA",
    category: "Cinematic Property Video",
    description: "Cinematic property video tour for a residential listing in Tumwater, WA — produced by Luminate Media.",
    uploadDate: "2025-12-01",
  },
  {
    id: "1162288739",
    title: "Revival Architects",
    location: "Seattle, WA",
    category: "Commercial / Architecture",
    description: "Commercial architecture video production for Revival Architects in Seattle, WA — produced by Luminate Media.",
    uploadDate: "2025-11-01",
  },
  {
    id: "1165998163",
    title: "Tellez for Circuit Court Judge",
    location: "Arkansas",
    category: "Campaign / Broadcast Video",
    description: "Broadcast-quality campaign video for Tellez for Circuit Court Judge — produced by Luminate Media in Arkansas.",
    uploadDate: "2025-12-01",
  },
];

const LazyVimeo = ({ id, title }: { id: string; title: string }) => {
  const [loaded, setLoaded] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${id}&width=1280`)
      .then((res) => res.json())
      .then((data) => {
        if (data.thumbnail_url) {
          setThumbUrl(data.thumbnail_url);
        }
      })
      .catch(() => {});
  }, [id]);

  if (loaded) {
    return (
      <iframe
        src={`https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&color=B8960C&autoplay=1`}
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title={title}
      />
    );
  }

  return (
    <button
      onClick={() => setLoaded(true)}
      className="absolute inset-0 w-full h-full bg-muted flex items-center justify-center group/play cursor-pointer border-0"
      aria-label={`Play video: ${title}`}
    >
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 bg-background/30" />
      <div className="relative z-10 w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center group-hover/play:bg-primary transition-colors">
        <Play className="text-primary-foreground ml-1" size={28} fill="currentColor" />
      </div>
    </button>
  );
};

const VideoSection = () => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@graph": featuredVideos.map((v) => ({
        "@type": "VideoObject",
        name: v.title,
        description: v.description,
        uploadDate: v.uploadDate,
        embedUrl: `https://player.vimeo.com/video/${v.id}`,
        thumbnailUrl: `https://vumbnail.com/${v.id}.jpg`,
        publisher: {
          "@type": "Organization",
          name: "Luminate Media",
          url: "https://luminate.media",
        },
      })),
    };
    let script = document.querySelector('script[data-schema="video"]') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "video");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    return () => {
      const el = document.querySelector('script[data-schema="video"]');
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
            Cinematic Work
          </p>
          <h2 className="font-serif text-3xl md:text-5xl font-semibold text-foreground mb-6">
            Motion That Sells
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From cinematic property tours to broadcast-quality commercial and campaign video —
            Luminate delivers visuals that move people.
          </p>
        </div>

        {/* Featured video — large */}
        <div className="mb-8 rounded-lg overflow-hidden shadow-gold">
          <div className="relative" style={{ padding: "56.25% 0 0 0" }}>
            <LazyVimeo id={featuredVideos[0].id} title={featuredVideos[0].title} />
          </div>
          <div className="bg-card border border-border border-t-0 px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-primary text-xs font-medium tracking-wide uppercase mb-1">
                {featuredVideos[0].category}
              </p>
              <p className="font-serif text-lg font-semibold text-foreground">
                {featuredVideos[0].title} · {featuredVideos[0].location}
              </p>
            </div>
          </div>
        </div>

        {/* Two smaller videos side by side */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {featuredVideos.slice(1).map((video) => (
            <div key={video.id} className="rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors">
              <div className="relative" style={{ padding: "56.25% 0 0 0" }}>
                <LazyVimeo id={video.id} title={video.title} />
              </div>
              <div className="bg-card px-5 py-4">
                <p className="text-primary text-xs font-medium tracking-wide uppercase mb-1">
                  {video.category}
                </p>
                <p className="font-serif text-base font-semibold text-foreground">
                  {video.title} · {video.location}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button variant="outline" size="lg" asChild>
            <Link to="/portfolio">
              View Full Portfolio
              <ArrowRight className="ml-2" size={18} />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
